import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ResidenceBanner, ResidenceCard, useCanonicalResidence } from '../../components/patient/ResidencePrompt';
import { PatientOfferTable, splitOfferScopes } from '../../components/patient/PatientOfferTable'; // SALES-CRM-SIMPLIFICATION
import { formatMoney } from '../../utils/money';
import { fetchOrderPaymentState, paymentLinkFor } from '../../utils/paymentEligibility';
import { useAuth } from '../../context/AuthContext';
import { usePatientFlow } from '../../context/PatientFlowContext';
import api from '../../utils/api';
import { formatDateLocale } from '../../utils/dateFormat';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { FileText, ShoppingCart, ClipboardList, Loader2, LogOut, Plus, ArrowRight, Bell, MessageCircle, ShieldCheck, CreditCard, Settings, Send, AlertCircle, Clock, Eye, CheckCircle2, HeartPulse } from 'lucide-react';
import { toast } from "../../hooks/use-toast";
import { toast as sonner } from "sonner";
import { Toaster } from '../../components/Toaster';
import { AftercarePanel } from '../../components/AftercarePanel';
// WORKFLOW-OPS-001-PHASE-2B — canonical patient treatment journey driven
// by treatment_cases / milestone_instances / milestone_tasks.
import PatientTreatmentJourney from '../../components/PatientTreatmentJourney';
// COA-10 G4 — operational task inbox driven by GET /api/patient/tasks
import PatientTaskInbox from './PatientTaskInbox';


/**
 * Null-safe locale-string formatter for monetary/numeric display.
 *
 * Contract:
 *   • valid finite number (including 0)  → formatted via toLocaleString()
 *   • undefined / null / NaN / non-number → neutral fallback (default "—")
 *
 * Never fabricates a value. Preserves the caller's currency/unit suffix,
 * which is applied at the call site independently of this helper.
 */
const fmtNumber = (v, fallback = '—') => {
  if (typeof v === 'number' && Number.isFinite(v)) return v.toLocaleString();
  const n = Number(v);
  if (Number.isFinite(n) && v !== null && v !== undefined && v !== '') return n.toLocaleString();
  return fallback;
};


const PatientDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const residence = useCanonicalResidence();
  const { resetFlow, updateFlowState } = usePatientFlow();
  // P7Z-3A: Contextual confirmation banner shown only when arriving from
  // the search → quote-request submit flow. Disappears on any subsequent
  // navigation. We snapshot the flag once on mount, then drop the router
  // state via a replace so a refresh of /patient/requests no longer shows it.
  const [submitContext] = useState(() => ({
    fromSearchSubmit: !!location.state?.fromSearchSubmit,
    count: Number(location.state?.count) || 0,
  }));
  useEffect(() => {
    if (submitContext.fromSearchSubmit) {
      // Replace history entry with an identical path but cleared state so a
      // hard refresh starts a fresh visit (no stale banner).
      navigate(location.pathname, { replace: true, state: null });
    }
  }, []);
  const fromSearchSubmit = submitContext.fromSearchSubmit;
  const submittedCount = submitContext.count;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [offerGroups, setOfferGroups] = useState([]); // LIFECYCLE-V3.2
  const [showRequestHistory, setShowRequestHistory] = useState(false); // LIFECYCLE-V3.2
  const [showOfferHistory, setShowOfferHistory] = useState(false); // SALES-CRM-SIMPLIFICATION — Offers tab Active/History
  const [orders, setOrders] = useState([]);
  // ORDER-CONTENT-01 — payment state per order, READ from the canonical
  // eligibility gate (never initiated here). Without it the dashboard could
  // not tell an unfinished payment from a settled or future-scheduled one,
  // so the patient had no way back to their own pending checkout.
  const [orderPayments, setOrderPayments] = useState({});
  const [intake, setIntake] = useState(null);
  // PERF-DASHBOARD-SECTIONS-01 — per-section truth: a failed request is an
  // error for THAT section, never an empty successful list.
  const [sectionErrors, setSectionErrors] = useState({ offers: null, orders: null, requests: null, intake: null });
  // PERF-DASHBOARD-SECTIONS-02 — the page used to hide EVERY section behind a
  // full-screen spinner until the slowest request settled, and an unloaded
  // section rendered as a confident "0" / "nothing yet". A section is now
  // either loading, loaded (its own data is on screen) or failed, and only a
  // loaded section is allowed to state a count or an empty result.
  const [sectionLoaded, setSectionLoaded] = useState({ offers: false, orders: false, requests: false, intake: false });
  const markLoaded = (key) => setSectionLoaded((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  const countOf = (key, value) => (sectionLoaded[key] ? value : '—');
  // P0-1: Requested quotes (patient-initiated quote requests)
  const [quoteRequests, setQuoteRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  // TESTING-FIX (iteration_419): these derived values referenced `quoteRequests`
  // before its useState declaration -> ReferenceError (TDZ) crashed the page.
  const activeGroups = offerGroups.filter(g => g.group_active);
  // ORDER-CONTENT-01 — the Offers card/tab counts must match what the Offers
  // table actually lists as Active (per-offer `quote_actionable`), otherwise
  // the number on the card sends the patient to a differently-sized list.
  const offerScopes = splitOfferScopes(quotes, offerGroups);
  const actionableOffers = offerScopes.active;
  const activeRequests = quoteRequests.filter(r => r.request_active !== false);
  const historyRequests = quoteRequests.filter(r => r.request_active === false);
  const visibleRequests = showRequestHistory ? historyRequests : activeRequests;
  // Fix #5: Notification dropdown
  const [showNotifications, setShowNotifications] = useState(false);
  // ORDER-CONTENT-01 — the Orders card must open the Orders view, so the tab
  // set is controlled rather than defaultValue-only.
  const [activeTab, setActiveTab] = useState('tasks');
  const ordersTabRef = React.useRef(null);
  const openOrdersTab = () => {
    setActiveTab('orders');
    // The `activeTab === 'orders'` effect performs the sweep; calling it here
    // as well issued every eligibility read twice (NAV-LATENCY-01).
    requestAnimationFrame(() => ordersTabRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  };
  const payableOrders = orders.filter(o => orderPayments[o.id]?.state === 'payable');

  // POST-PAYMENT-TRUTH-01 — the journey indicator reads the client-side flow
  // state, which lives in this browser only: on another device, or after the
  // storage is cleared, a patient who had already paid was shown the journey as
  // if no money had ever been collected. The canonical per-order payment facts
  // the dashboard ALREADY holds are the authority, so they hydrate it.
  React.useEffect(() => {
    const anyCollected = Object.values(orderPayments).some(
      (p) => p?.state === 'settled' || Number(p?.data?.settled_total) > 0);
    if (anyCollected) updateFlowState({ paymentCompleted: true });
  }, [orderPayments, updateFlowState]);
  // TREATX-11Y-PATIENT-DASHBOARD-HOOKS-01 — canonical patient feed
  // (notifications_log via NotificationService). No proxy data here.
  const [canonicalNotifs, setCanonicalNotifs] = useState([]);
  const [canonicalUnread, setCanonicalUnread] = useState(0);

  const refreshCanonicalNotifs = React.useCallback(async () => {
    try {
      const [list, cnt] = await Promise.all([
        api.get('/patient/notifications', { params: { limit: 8 } }),
        api.get('/patient/notifications/unread-count'),
      ]);
      setCanonicalNotifs(((list.data && list.data.notifications) || []));
      setCanonicalUnread(((cnt.data && cnt.data.unread) || 0));
    } catch (e) {
      // canonical fallback: leave bell empty rather than show proxy data.
      setCanonicalNotifs([]);
      setCanonicalUnread(0);
    }
  }, []);

  useEffect(() => { refreshCanonicalNotifs(); }, [refreshCanonicalNotifs]);

  // Opening the Orders view by ANY route (card or tab) resolves the rows the
  // first bounded batch did not cover.
  useEffect(() => {
    if (activeTab === 'orders') void resolveRemainingOrderStates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, orders.length]);

  const markCanonicalRead = async (id) => {
    try {
      await api.post(`/patient/notifications/${id}/mark-read`);
      await refreshCanonicalNotifs();
    } catch (e) {
      // Silent — list will refresh on next bell open.
    }
  };

  // Fix #4: Relative time helper
  const timeAgo = (dateStr) => {
    if (!dateStr) return '';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return t('patient.dashboard.timeJustNow');
    if (mins < 60) return t('patient.dashboard.timeMinsAgo', { mins });
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return t('patient.dashboard.timeHrsAgo', { hrs });
    const days = Math.floor(hrs / 24);
    if (days < 7) return t('patient.dashboard.timeDaysAgo', { days });
    // TREATX-BATCH-B1.2 — patient-facing dates use the canonical
    // DD/MM/YYYY presentation authority. Bare toLocaleDateString()
    // yields locale-dependent output (M/D/YYYY on en-US) and creates
    // date-format drift against MyOffers/Compare/PDF.
    return formatDateLocale(dateStr);
  };

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
    }
  }, [user]);

  // B2 freshness: server truth is synchronous; refetch projections when the tab regains focus/visibility
  useEffect(() => {
    if (!user?.id) return undefined;
    const refresh = () => { if (document.visibilityState === 'visible') fetchDashboardData({ silent: true }); };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => { window.removeEventListener('focus', refresh); document.removeEventListener('visibilitychange', refresh); };
  }, [user]);

  const fetchDashboardData = async ({ silent = false } = {}) => {
    // PERF-DASHBOARD-SECTIONS-02 — `gen` MUST live in function scope: declared
    // inside `try`, the `finally` block below referenced a binding that is not
    // in its scope and every load ended in `ReferenceError: gen is not defined`.
    const gen = loadGenRef.current + 1;
    loadGenRef.current = gen;
    const fresh = () => gen === loadGenRef.current;
    try {
      if (!silent) setLoading(true);
      const queryId = user?.person_id || user?.id;
      // PERF-AUDIT-2026-06: `/patient/quote-requests` has no dependency on the
      // other three reads, but used to be awaited AFTER them — adding a second
      // serial network wave (measured +200–600 ms) to every patient dashboard
      // mount. It now runs in the SAME parallel batch.
      // PERF-DASHBOARD-SECTIONS-01 — the four reads were issued in parallel but
      // PUBLISHED together, so the whole dashboard waited for the slowest of
      // them (and a single failure emptied every section). Each section now
      // publishes as soon as its OWN request resolves, under the same
      // generation token that already protected payment eligibility, so a
      // superseded load can never overwrite a newer one. A failed request sets
      // that section's error — it is never published as an empty success.
      const offersPromise = api.get(`/patient/quotes/${queryId}`)
        .then((res) => {
          if (!fresh()) return null;
          // P0-2: Filter quotes to visible statuses — must match MyOffers exactly
          const visibleStatuses = ['SENT', 'VIEWED', 'ACCEPTED', 'EXPIRED', 'REJECTED'];
          setQuotes((res.data.quotes || []).filter((q) => visibleStatuses.includes(q.status)));
          // LIFECYCLE-V3.2 — server-side projection authority
          setOfferGroups(res.data.offer_groups || []);
          setSectionErrors((prev) => ({ ...prev, offers: null }));
          markLoaded('offers');
          setError(null);
          return res;
        })
        .catch((e) => {
          if (fresh()) setSectionErrors((prev) => ({ ...prev, offers: 'failed' }));
          throw e;
        });
      const ordersPromise = api.get(`/patient/orders/${user?.id}`)
        .then((res) => {
          if (fresh()) {
            setOrders(res.data.orders || []);
            setSectionErrors((prev) => ({ ...prev, orders: null }));
            markLoaded('orders');
            setError(null);
          }
          return res;
        })
        .catch((e) => {
          if (fresh()) setSectionErrors((prev) => ({ ...prev, orders: 'failed' }));
          throw e;
        });
      const intakePromise = api.get(`/patient/onboarding/${user?.id}`)
        .then((res) => {
          if (fresh()) {
            setIntake(res.data);
            setSectionErrors((prev) => ({ ...prev, intake: null }));
            markLoaded('intake');
          }
          return res;
        })
        .catch(() => { if (fresh()) setSectionErrors((prev) => ({ ...prev, intake: 'failed' })); return null; });
      // PERF-DASHBOARD-SECTIONS-02 — the helper used to swallow its own failure
      // and return [], so this `.catch` never ran and the caller cleared the
      // section error as if "no requests" were the truth. It now rejects.
      const requestsPromise = fetchQuoteRequests(gen)
        .then((rows) => {
          if (fresh()) {
            setSectionErrors((prev) => ({ ...prev, requests: null }));
            markLoaded('requests');
            setError(null);
          }
          return rows;
        })
        .catch((e) => {
          if (fresh()) setSectionErrors((prev) => ({ ...prev, requests: 'failed' }));
          throw e;
        });

      // Payment eligibility needs BOTH orders and requests, which is a real
      // dependency — it waits for those two only, not for the whole page.
      const eligibility = Promise.all([ordersPromise, requestsPromise])
        .then(([ordersRes, requestRows]) => {
          if (!fresh()) return;
          void loadOrderPaymentStates(ordersRes.data.orders || [], requestRows || [], gen);
        })
        .catch(() => {});

      const settled = await Promise.allSettled(
        [offersPromise, ordersPromise, intakePromise, requestsPromise, eligibility]);
      if (!fresh()) return;
      // Only a page with NOTHING usable is a page-level error.
      const required = [settled[0], settled[1], settled[3]];
      if (required.every((r) => r.status === 'rejected')) {
        throw required[0].reason || new Error('dashboard_unavailable');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      // A SUPERSEDED load must never replace a newer successful page with an
      // error screen or a toast.
      if (!fresh()) return;
      // Content already on screen stays; a background refresh failure is told
      // through the section banner, not by blanking the page.
      if (Object.values(sectionLoaded).some(Boolean) || silent) {
        setSectionErrors((prev) => ({ ...prev, offers: prev.offers || 'failed' }));
        return;
      }
      setError(t('patient.dashboard.somethingWentWrong'));
      toast({
        title: t('patient.common.error'),
        description: t('patient.dashboard.somethingWentWrong'),
        variant: 'destructive'
      });
    } finally {
      if (fresh()) setLoading(false);
    }
  };

  // P0-1: Fetch quote requests
  // PAYABLE-ORDER-VISIBILITY-01 — the first batch stays bounded so the
  // first screen paints fast, but it no longer PERMANENTLY excludes the
  // remaining orders: opening the Orders view resolves the rest in bounded
  // waves through the same eligibility gate. `loadGenRef` discards the
  // results of a superseded dashboard load instead of letting them overwrite
  // a newer one.
  const loadGenRef = React.useRef(0);
  const [payStatesResolving, setPayStatesResolving] = useState(false);

  const applyPaymentStates = (gen, entries) => {
    if (gen !== loadGenRef.current) return;
    setOrderPayments(prev => ({ ...prev, ...Object.fromEntries(entries) }));
  };

  // NAV-LATENCY-01 — the initial batch and the Orders-view sweep could both
  // ask for the SAME order's eligibility (and `openOrdersTab` plus its effect
  // fired the sweep twice), so the dashboard issued duplicate contract reads.
  // One in-flight promise per order is shared instead; the generation token
  // still decides who may publish, so stale answers remain discarded. This
  // caches an in-flight REQUEST for the current page lifetime only — never an
  // authorization decision, and never across users.
  const inFlightStatesRef = React.useRef(new Map());

  const resolveOneState = React.useCallback((id) => {
    const existing = inFlightStatesRef.current.get(id);
    if (existing) return existing;
    const p = fetchOrderPaymentState(id)
      .finally(() => inFlightStatesRef.current.delete(id));
    inFlightStatesRef.current.set(id, p);
    return p;
  }, []);

  const resolvePaymentStates = React.useCallback(async (ids, gen) => {
    const CHUNK = 4;
    for (let i = 0; i < ids.length; i += CHUNK) {
      if (gen !== loadGenRef.current) return;
      const slice = ids.slice(i, i + CHUNK);
      const entries = await Promise.all(
        slice.map(async (id) => [id, await resolveOneState(id)]),
      );
      applyPaymentStates(gen, entries);
    }
  }, [resolveOneState]);

  const loadOrderPaymentStates = async (orderRows, requestRows, gen) => {
    const flagged = new Set(
      (requestRows || []).map(r => r.payment_entry?.order_id).filter(Boolean),
    );
    const first = (orderRows || [])
      .filter((o, idx) => flagged.has(o.id) || idx < 3)
      .slice(0, 8)
      .map(o => o.id);
    await resolvePaymentStates(first, gen);
  };

  // Resolve every order still without a state when the Orders view opens.
  const resolveRemainingOrderStates = React.useCallback(async (force = false) => {
    const gen = loadGenRef.current;
    const pending = orders
      .map(o => o.id)
      .filter(id => force
        ? ['unknown', 'incomplete', 'activation_pending'].includes(orderPayments[id]?.state) || !orderPayments[id]
        : !orderPayments[id]);
    if (!pending.length) return;
    setPayStatesResolving(true);
    try {
      await resolvePaymentStates(pending, gen);
    } finally {
      if (gen === loadGenRef.current) setPayStatesResolving(false);
    }
  }, [orders, orderPayments, resolvePaymentStates]);

  // PERF-DASHBOARD-SECTIONS-02 — this helper used to convert an API failure
  // into successful empty data (`return []`), so the caller could not tell
  // "no requests" from "the request list could not be loaded", and it
  // published state outside the caller's load generation. It now rejects and
  // only publishes while its own load is still the active one.
  const fetchQuoteRequests = async (gen) => {
    const owns = () => gen === undefined || gen === loadGenRef.current;
    if (owns()) setRequestsLoading(true);
    try {
      const res = await api.get('/patient/quote-requests');
      const rows = res.data.quote_requests || [];
      if (owns()) {
        setQuoteRequests(rows);
        setRequestsLoading(false);
      }
      return rows;
    } catch (err) {
      console.error('Failed to fetch quote requests:', err);
      if (owns()) setRequestsLoading(false);
      throw err;
    }
  };

  // REQUEST-LEAD-QUOTE-STATE — explicit Close Request (≠ Reject Offer). In-app
  // confirmation (native window.confirm blocked the main thread in UAT).
  const [closeRequestTarget, setCloseRequestTarget] = useState(null);
  const handleCloseRequest = (req) => setCloseRequestTarget(req);
  const confirmCloseRequest = async () => {
    const req = closeRequestTarget;
    if (!req) return;
    try {
      await api.post(`/patient/quote-requests/${req.id}/close`, {});
      toast({ title: t('patient.dashboard.closeRequestSuccess'), duration: 6000 });
      setCloseRequestTarget(null);
      try {
        await fetchQuoteRequests(loadGenRef.current);
      } catch (requestsErr) {
        console.error('Failed to refresh quote requests after close:', requestsErr);
        setSectionErrors((prev) => ({ ...prev, requests: 'failed' }));
      }
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.message || err.message, variant: 'destructive' });
    }
  };


  const getOrderStatusColor = (status) => {
    const colors = {
      'PENDING_CONFIRMATION': 'bg-yellow-100 text-yellow-800',
      'CONFIRMED': 'bg-blue-100 text-blue-800',
      'IN_PROGRESS': 'bg-purple-100 text-purple-800',
      'COMPLETED': 'bg-green-100 text-green-800',
      'CANCELLED': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // PERF-DASHBOARD-SECTIONS-02 — no full-page spinner: the shell, navigation
  // and every already-loaded section render immediately; each section carries
  // its own loading/failed state. The page-level error screen is reserved for
  // a load where NOTHING became available.
  const SectionPending = ({ testid }) => (
    <div className="text-center py-6" data-testid={testid}>
      <Loader2 className="h-6 w-6 animate-spin text-blue-600 mx-auto" />
    </div>
  );

  const SectionFailed = ({ testid }) => (
    <div className="text-center py-6" data-testid={testid}>
      <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
      <p className="text-sm text-slate-600 mb-3">{t('patient.dashboard.sectionUnavailable')}</p>
      <Button size="sm" variant="outline" onClick={() => fetchDashboardData({ silent: true })} data-testid={`${testid}-retry`}>
        {t('patient.dashboard.tryAgain')}
      </Button>
    </div>
  );

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50" data-testid="patient-dashboard-error">
        <div className="text-center max-w-md px-4">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-gray-900 mb-2">{t('patient.dashboard.somethingWentWrong')}</h2>
          <p className="text-sm text-gray-600 mb-4">{error}</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => { setError(null); setLoading(true); fetchDashboardData(); }} data-testid="retry-btn">
              {t('patient.dashboard.tryAgain')}
            </Button>
            <Button variant="outline" onClick={() => navigate('/')} data-testid="go-home-btn">
              {t('patient.dashboard.goHome')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12">
      <Toaster />
      {loading && (
        <div className="h-0.5 w-full bg-blue-100 overflow-hidden" data-testid="patient-dashboard-refreshing">
          <div className="h-full w-1/3 bg-blue-600 animate-pulse" />
        </div>
      )}
      {/* PERF-DASHBOARD-SECTIONS-01 — sections publish independently, so a
          section that FAILED says so and offers a retry instead of appearing
          as an empty, successful list. */}
      {Object.values(sectionErrors).some(Boolean) && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4" data-testid="dashboard-section-error">
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-center justify-between gap-3">
            <span>{t('patient.dashboard.sectionUnavailable')}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => fetchDashboardData({ silent: true })}
              data-testid="dashboard-section-retry"
            >
              {t('patient.dashboard.tryAgain')}
            </Button>
          </div>
        </div>
      )}
      
      {/* P3-1: Header focused on "Where am I in my treatment journey?" */}
      <header className="bg-white border-b mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="patient-dashboard-title">
                {t('patient.dashboard.myJourney')}
              </h1>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                {t('patient.journey.subtitle')}
              </p>
            </div>
            <div className="flex gap-3">
              <Button 
                onClick={() => {
                  const ONBOARD_MAP = {
                    'Dental': 'dental',
                    'Hair Transplant': 'aesthetics',
                    'Aesthetic Surgery': 'aesthetics',
                    'Bariatric / Weight Loss': 'bariatric',
                    'Orthopedics': 'orthopedic',
                    'Dermatology / Skin': 'dermatology',
                    'Fertility': 'fertility',
                    "Women's Health": 'wellness',
                    "Men's Health": 'wellness',
                  };
                  const slugs = [...new Set(
                    (intake?.treatment_categories || [])
                      .map(label => ONBOARD_MAP[label])
                      .filter(Boolean)
                  )];
                  resetFlow();
                  navigate('/find-clinics', { state: { treatmentSlugs: slugs } });
                }}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                data-testid="new-treatment-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('patient.dashboard.browseTreatments')}
              </Button>
              {/* TREATX-11Y bell — canonical notifications_log feed */}
              <div className="relative">
                <Button 
                  variant="outline" 
                  size="icon" 
                  onClick={() => { setShowNotifications(!showNotifications); refreshCanonicalNotifs(); }} 
                  data-testid="notification-bell-btn"
                  className="relative"
                >
                  <Bell className="h-4 w-4" />
                  {canonicalUnread > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-bold" data-testid="notification-badge">
                      {canonicalUnread}
                    </span>
                  )}
                </Button>
                {showNotifications && (
                  <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-slate-200 z-50 overflow-hidden" data-testid="notification-dropdown">
                    <div className="px-4 py-3 bg-slate-50 border-b flex items-center justify-between">
                      <span className="font-medium text-sm text-slate-700">{t('patient.dashboard.notifications')}</span>
                      <button
                        className="text-xs text-blue-600 hover:underline"
                        onClick={() => { setShowNotifications(false); navigate('/patient/notifications'); }}
                        data-testid="notif-view-all"
                      >
                        {t('patient.common.seeAll')}
                      </button>
                    </div>
                    <div className="max-h-72 overflow-y-auto">
                      {canonicalNotifs.length === 0 ? (
                        <div className="px-4 py-6 text-center text-sm text-slate-400" data-testid="no-notifications">
                          <Bell className="h-8 w-8 mx-auto mb-2 text-slate-200" />
                          {t('patient.notifications.empty')}
                        </div>
                      ) : (
                        canonicalNotifs.map((n) => (
                          <button
                            key={n.id}
                            onClick={() => { if (!n.read) markCanonicalRead(n.id); }}
                            className={`w-full text-left px-4 py-3 border-b border-slate-100 transition-colors ${n.read ? 'bg-white' : 'bg-blue-50 hover:bg-blue-100'}`}
                            data-testid={`notif-canonical-${n.id}`}
                          >
                            <div className="flex items-start gap-2.5">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${n.read ? 'bg-slate-100' : 'bg-blue-100'}`}>
                                <Bell className={`h-4 w-4 ${n.read ? 'text-slate-400' : 'text-blue-600'}`} />
                              </div>
                              <div className="min-w-0 flex-1">
                                {/* TREATX-BATCH-B1.1 CLOSURE — subject
                                    carries the canonical commercial
                                    amount (e.g. "New offer from … —
                                    6,269"). Truncating clips the amount
                                    out of view; wrap on two lines
                                    instead so the visible bell always
                                    shows the canonical Total. */}
                                <p
                                  className={`text-sm ${n.read ? 'text-slate-600' : 'text-slate-900 font-medium'} break-words whitespace-normal leading-snug`}
                                  data-testid={`notif-subject-${n.id}`}
                                >
                                  {n.subject || n.event || t('patient.dashboard.notificationFallback')}
                                </p>
                                <p className="text-[11px] text-slate-400 mt-0.5">
                                  {n.created_at ? formatDateLocale(n.created_at) + ' ' + new Date(n.created_at).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' }) : ''}
                                  {!n.read ? ` · ${t('patient.dashboard.clickToMarkRead')}` : ''}
                                </p>
                              </div>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
              <Button variant="outline" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="h-4 w-4 mr-2" />
                {t('patient.shell.signOut')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b" data-testid="patient-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 overflow-x-auto py-1">
            {[
              { label: t('adminNav.dashboard'), path: '/patient/dashboard', icon: ClipboardList, active: true, key: 'dashboard' },
              { label: t('patient.dashboard.myOffers'), path: '/patient/offers', icon: FileText, key: 'offers' },
              { label: t('patient.dashboard.myMessages'), path: '/patient/messages', icon: MessageCircle, key: 'messages' },
              { label: t('patient.dashboard.myDocuments'), path: '/patient/medical-files', icon: ShieldCheck, key: 'files' },
              { label: t('patient.consent.beforeAfterTitle'), path: '/patient/consent/before-after', icon: ShieldCheck, key: 'consents' },
              { label: t('patient.payment.history'), path: '/patient/payment-history', icon: CreditCard, key: 'payments' },
              { label: t('patient.dashboard.settings'), path: '/patient/settings/notifications', icon: Settings, key: 'settings' },
              { label: t('patient.quote.termsLink'), path: '/patient/settings/legal', icon: FileText, key: 'legal' },
            ].map(item => (
              <Button
                key={item.path}
                variant={item.active ? 'default' : 'ghost'}
                size="sm"
                className={`text-xs whitespace-nowrap ${item.active ? 'bg-blue-600 text-white' : 'text-slate-600'}`}
                onClick={() => navigate(item.path)}
                data-testid={`nav-${item.key}`}
              >
                <item.icon className="h-3.5 w-3.5 mr-1.5" />
                {item.label}
              </Button>
            ))}
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        {residence.declared === false && (
          <div className="mb-4"><ResidenceBanner onDeclared={() => residence.refresh()} /></div>
        )}
        {closeRequestTarget && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[60] flex items-center justify-center p-4" data-testid="close-request-confirm-modal">
            <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
              <h3 className="text-lg font-semibold text-[#122033]">{t('patient.dashboard.closeRequest')}</h3>
              <p className="text-sm text-slate-600">{t('patient.dashboard.closeRequestConfirm')}</p>
              <div className="flex gap-3 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => setCloseRequestTarget(null)} data-testid="close-request-cancel">Cancel</Button>
                <Button variant="destructive" className="flex-1" onClick={confirmCloseRequest} data-testid="close-request-confirm">{t('patient.dashboard.closeRequest')}</Button>
              </div>
            </div>
          </div>
        )}
        {/* ORDER-CONTENT-01 — the unfinished payment, first on the page so it
            is reachable on a phone without scrolling past the counters. One
            row per accepted order that has a CHARGEABLE obligation right now
            (canonical eligibility), carrying order_id AND quote_id into the
            existing PaymentPage flow. */}
        {payableOrders.length > 0 && (
          <div className="mb-4 space-y-2" data-testid="resume-payment-banner">
            {payableOrders.map((order) => {
              const elig = orderPayments[order.id]?.data || {};
              return (
                <div
                  key={order.id}
                  className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3"
                  data-testid={`resume-payment-row-${order.id}`}
                >
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-emerald-900">
                      {t('patient.dashboard.paymentWaiting')}
                    </p>
                    <p className="text-xs text-emerald-800 truncate">
                      {order.clinic_info?.clinic_name || ''}
                      {elig.treatment ? ` · ${elig.treatment}` : ''}
                      {Number.isFinite(elig.amount)
                        ? ` · ${t('patient.orderDetail.dueNow')}: ${formatMoney(elig.amount, elig.currency || order.currency)}`
                        : ''}
                    </p>
                  </div>
                  <Button
                    size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                    onClick={() => navigate(paymentLinkFor(order.id, elig.quote_id || order.quote_id))}
                    data-testid={`resume-payment-cta-${order.id}`}
                  >
                    <CreditCard className="h-4 w-4 mr-1.5" />
                    {t('patient.dashboard.continueToPayment')}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        <div className="mb-4"><ResidenceCard residence={residence} /></div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/patient/onboarding')} data-testid="intake-status-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('patient.dashboard.treatmentPreferences')}</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              {!sectionLoaded.intake && !sectionErrors.intake ? (
                <SectionPending testid="intake-card-loading" />
              ) : intake ? (
                <>
                  <div className="text-2xl font-bold text-green-600">{t('patient.dashboard.preferencesSet')}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {t('patient.dashboard.treatmentsSelected', { count: intake.treatment_categories?.length || 0 })}
                  </p>
                  <Button 
                    variant="link" 
                    className="px-0 mt-2 text-blue-600"
                    onClick={() => navigate('/patient/onboarding')}
                  >
                    {t('patient.dashboard.updatePreferences')}
                  </Button>
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold text-orange-600">{t('patient.dashboard.preferencesNotSet')}</div>
                  <Button 
                    variant="link" 
                    className="px-0 mt-2 text-blue-600"
                    onClick={() => navigate('/patient/onboarding')}
                  >
                    {t('patient.dashboard.setPreferences')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>

          {/* M16B-03A — first-class entry to the new Clinical Profile editor.
              This card is distinct from the legacy "Treatment preferences"
              wizard (which captures intake demographics/budget/travel) and
              points to the new structured medical profile editor.          */}
          <Card className="hover:shadow-lg transition-shadow cursor-pointer ring-1 ring-emerald-100" onClick={() => navigate('/patient/clinical-profile/edit')} data-testid="medical-profile-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('patient.dashboard.medicalProfile')}</CardTitle>
              <HeartPulse className="h-4 w-4 text-emerald-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-700" data-testid="medical-profile-card-status">{t('patient.dashboard.medicalProfileStatus')}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {t('patient.dashboard.medicalProfileDesc')}
              </p>
              <Button
                variant="link"
                className="px-0 mt-2 text-emerald-700"
                onClick={(e) => { e.stopPropagation(); navigate('/patient/clinical-profile/edit'); }}
                data-testid="medical-profile-card-cta"
              >
                {t('patient.dashboard.openClinicalProfile')}
              </Button>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/patient/offers')} data-testid="active-quotes-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('patient.dashboard.activeOffers')}</CardTitle>
              <ClipboardList className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600" data-testid="active-offers-count">{countOf('offers', actionableOffers.length)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {sectionLoaded.offers
                  ? t('patient.dashboard.pendingReview', { count: actionableOffers.filter(q => q.status === 'SENT' || q.status === 'VIEWED').length })
                  : t('patient.dashboard.sectionLoadingShort')}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/patient/offers')} data-testid="my-offers-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('patient.dashboard.myOffers')}</CardTitle>
              <FileText className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600" data-testid="my-offers-count">{countOf('offers', quotes.length)}</div>
              <p className="text-xs text-muted-foreground mt-1">
                {sectionLoaded.offers
                  ? t('patient.dashboard.newOffersToReview', { count: actionableOffers.filter(q => q.status === 'SENT').length })
                  : t('patient.dashboard.sectionLoadingShort')}
              </p>
            </CardContent>
          </Card>

          <Card className="hover:shadow-lg transition-shadow cursor-pointer" onClick={openOrdersTab} data-testid="orders-card">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{t('patient.dashboard.orders')}</CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600" data-testid="orders-count">{countOf('orders', orders.length)}</div>
              <p className="text-xs text-muted-foreground mt-1" data-testid="orders-awaiting-payment">
                {sectionLoaded.orders
                  ? t('patient.dashboard.ordersAwaitingPayment', { count: payableOrders.length })
                  : t('patient.dashboard.sectionLoadingShort')}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Intake Summary */}
        {intake && (
          <Card className="mb-4 border-blue-200 bg-blue-50/30">
            <CardHeader>
              <CardTitle className="text-blue-900">{t('patient.dashboard.myTreatmentPreferences')}</CardTitle>
              <CardDescription>{t('patient.dashboard.intakeSummary')}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="font-medium text-gray-600">{t('patient.dashboard.genderLabel')}</span> <span className="text-gray-900">{intake.gender}</span>
                </div>
                <div>
                  <span className="font-medium text-gray-600">{t('patient.dashboard.countryLabel')}</span> <span className="text-gray-900">{intake.country_of_origin}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="font-medium text-gray-600 block mb-2">{t('patient.dashboard.treatmentsOfInterest')}</span>
                  <div className="flex flex-wrap gap-2">
                    {(intake.treatment_categories || []).map((treatment, idx) => (
                      <Badge key={idx} className="bg-blue-100 text-blue-800 hover:bg-blue-200">{treatment}</Badge>
                    ))}
                  </div>
                </div>
                {intake.pricing_profile && intake.pricing_profile.length > 0 && (
                  <div className="md:col-span-2">
                    <span className="font-medium text-gray-600 block mb-2">{t('patient.dashboard.pricingProfile')}</span>
                    <div className="flex flex-wrap gap-2">
                      {intake.pricing_profile.map((profile, idx) => (
                        <Badge key={idx} className="bg-purple-100 text-purple-800 hover:bg-purple-200">{profile}</Badge>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* WORKFLOW-OPS-001-PHASE-2B (P0-A/B/C/D/E) — canonical patient
            treatment journey replaces the previous hard-coded stepper.
            Source of truth: treatment_cases + milestone_instances +
            milestone_tasks (sanitized via GET /api/cases/:id/patient-journey).
            Read-only by design; no assignment / status mutation surfaced
            to the patient. */}
        <PatientTreatmentJourney />

        {/* P7Z-3A: contextual confirmation banner — appears only when
            arriving directly from the search → request-offers submit flow.
            Subtle, non-blocking, disappears on any subsequent navigation. */}
        {fromSearchSubmit && (
          <div
            className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2.5 mb-3 flex items-start gap-2"
            data-testid="requests-submit-confirmation"
          >
            <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-emerald-900">
              <span className="font-semibold">
                {t('patient.dashboard.yourRequestsSent', { count: submittedCount })}
              </span>{' '}
              <span className="text-emerald-800/90">{t('patient.dashboard.providersRespond')}</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-3">
          <TabsList ref={ordersTabRef} className="grid w-full md:w-auto md:inline-grid grid-cols-2 md:grid-cols-5 gap-4">
            <TabsTrigger value="tasks" className="data-[state=active]:bg-rose-600 data-[state=active]:text-white" data-testid="tab-clinic-tasks">
              <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
              {t('patient.dashboard.tabTasksFromClinic')}
            </TabsTrigger>
            <TabsTrigger value="requests" className="data-[state=active]:bg-amber-600 data-[state=active]:text-white" data-testid="tab-your-requests">
              <Clock className="h-3.5 w-3.5 mr-1.5" />
              {t('patient.dashboard.tabYourRequests')} ({countOf('requests', activeRequests.length)})
            </TabsTrigger>
            <TabsTrigger value="quotes" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" data-testid="tab-offers-from-clinics">
              <FileText className="h-3.5 w-3.5 mr-1.5" />
              {t('patient.dashboard.tabOffersFromClinics')} ({countOf('offers', actionableOffers.length)})
            </TabsTrigger>
            <TabsTrigger value="orders" className="data-[state=active]:bg-purple-600 data-[state=active]:text-white" data-testid="tab-my-orders">
              <ShoppingCart className="h-3.5 w-3.5 mr-1.5" />
              {t('patient.dashboard.tabMyOrders')} ({countOf('orders', orders.length)})
            </TabsTrigger>
            <TabsTrigger value="aftercare" className="data-[state=active]:bg-emerald-600 data-[state=active]:text-white" data-testid="tab-aftercare">
              <HeartPulse className="h-3.5 w-3.5 mr-1.5" />
              {t('patient.dashboard.tabAftercare')}
            </TabsTrigger>
          </TabsList>

          {/* COA-10 G4 — Tasks from your clinic */}
          <TabsContent value="tasks">
            <PatientTaskInbox />
          </TabsContent>

          {/* Your Requests Tab — waiting state visual */}
          <TabsContent value="requests">
            <Card className="border-l-4 border-l-amber-400">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-500" /> {t('patient.dashboard.yourRequests')}
                </CardTitle>
                <CardDescription className="text-xs">{t('patient.dashboard.yourRequestsDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {requestsLoading && !sectionLoaded.requests ? (
                  <SectionPending testid="requests-section-loading" />
                ) : sectionErrors.requests && !sectionLoaded.requests ? (
                  <SectionFailed testid="requests-section-failed" />
                ) : (
                  <>
                  <div className="flex gap-2 mb-3" data-testid="requests-scope-toggle">
                    <Button size="sm" variant={showRequestHistory ? 'outline' : 'default'} onClick={() => setShowRequestHistory(false)} data-testid="requests-scope-active">{t('patient.offers.scopeActive')} ({activeRequests.length})</Button>
                    <Button size="sm" variant={showRequestHistory ? 'default' : 'outline'} onClick={() => setShowRequestHistory(true)} data-testid="requests-scope-history">{t('patient.offers.scopeHistory')} ({historyRequests.length})</Button>
                  </div>
                  {visibleRequests.length === 0 ? (
                  <div className="text-center py-6 text-gray-500" data-testid="no-requests-state">
                    <Send className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-2">{t('patient.dashboard.noQuoteRequests')}</p>
                    <p className="text-sm mb-4">{t('patient.dashboard.noRequestsHint')}</p>
                    <Button onClick={() => navigate('/find-clinics')} className="bg-teal-600 hover:bg-teal-700" data-testid="find-clinics-btn">
                      {t('patient.dashboard.findClinics')}
                    </Button>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('patient.dashboard.colClinic')}</TableHead>
                          <TableHead>{t('patient.dashboard.colRequestedTreatment')}</TableHead>
                          <TableHead>{t('patient.dashboard.colStatus')}</TableHead>
                          <TableHead>{t('patient.dashboard.colRequested')}</TableHead>
                          <TableHead className="text-right">{t('patient.dashboard.colActions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visibleRequests.map((req) => {
                          const lifecycle = req.lifecycle || 'Requested';
                          const lifecycleConfig = {
                            'Requested':   { color: 'bg-amber-100 text-amber-800', label: t('patient.dashboard.lifecycle_Requested') },
                            'Assigned':    { color: 'bg-sky-100 text-sky-800', label: t('patient.dashboard.lifecycle_Assigned') },
                            'In Progress': { color: 'bg-blue-100 text-blue-800', label: t('patient.dashboard.lifecycle_InProgress') },
                            'Offered':     { color: 'bg-purple-100 text-purple-800', label: t('patient.dashboard.lifecycle_Offered') },
                            'Accepted':    { color: 'bg-green-100 text-green-800', label: t('patient.dashboard.lifecycle_Accepted') },
                            'AcceptedAwaitingPayment': { color: 'bg-emerald-100 text-emerald-800', label: t('patient.dashboard.lifecycle_AcceptedAwaitingPayment') },
                            'On Hold':     { color: 'bg-slate-100 text-slate-600', label: t('patient.dashboard.lifecycle_OnHold') },
                            'Rejected':    { color: 'bg-gray-100 text-gray-600', label: t('patient.dashboard.lifecycle_Rejected') },
                            'Offer Declined': { color: 'bg-orange-100 text-orange-700', label: t('patient.dashboard.lifecycle_OfferDeclined') },
                            'Closed':      { color: 'bg-gray-200 text-gray-600', label: req.closed_by === 'patient' ? t('patient.dashboard.lifecycle_ClosedByYou') : t('patient.dashboard.lifecycle_Closed') },
                            'Expired':     { color: 'bg-red-100 text-red-600', label: t('patient.dashboard.lifecycle_Expired') },
                          };
                          const cfg = lifecycleConfig[lifecycle] || { color: 'bg-slate-100 text-slate-600', label: String(lifecycle), unknown: true };
                          return (
                            <TableRow key={req.id} className="hover:bg-teal-50/50" data-testid={`request-row-${req.id}`}>
                              <TableCell>
                                <div className="font-medium">{req.clinic_name}</div>
                                {(req.clinic_city || req.clinic_country) && (
                                  <div className="text-xs text-gray-500">
                                    {[req.clinic_city, req.clinic_country].filter(Boolean).join(', ')}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="text-sm" data-testid={`request-treatment-${req.id}`}>{req.treatment_display_name || req.treatment || t('patient.dashboard.generalInquiry')}</div>
                                {req.has_offer && req.offer_summary && (
                                  <div className="text-xs text-purple-600 mt-0.5">
                                    {req.offer_summary.currency} {fmtNumber(req.offer_summary?.price, '0')}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <Badge className={cfg.color} data-testid={`request-status-${req.id}`}>
                                  {cfg.label}
                                </Badge>
                                {/* RENEWAL-CYCLE-01 — a renewed request is its own
                                    request cycle, not a relabelled old one. The
                                    "awaiting a new offer" wording belongs ONLY to
                                    a cycle the projection reports as still waiting
                                    (`awaiting_new_offer`); a later cycle whose offer
                                    is already accepted gets a neutral cycle label,
                                    so the row cannot say "awaiting a new offer"
                                    next to an accepted order. */}
                                {(req.request_cycle || 1) > 1 && (
                                  <div className="mt-1 text-[10px] text-sky-700" data-testid={`request-renewed-${req.id}`}>
                                    {req.awaiting_new_offer
                                      ? t('patient.dashboard.requestRenewed', { cycle: req.request_cycle })
                                      : t('patient.dashboard.requestCycle', { cycle: req.request_cycle })}
                                  </div>
                                )}
                                {req.payment_entry && (
                                  <div className="mt-1 text-[10px] text-emerald-700 font-medium" data-testid={`request-awaiting-payment-${req.id}`}>
                                    {t('patient.dashboard.offerAcceptedAwaitingPayment')}
                                  </div>
                                )}
                                {/* Fix #4: Confidence signals */}
                                {(lifecycle === 'Assigned' || lifecycle === 'In Progress') && (
                                  <div className="flex items-center gap-1 mt-1 text-[10px] text-sky-600">
                                    <Eye className="h-3 w-3" /> {t('patient.dashboard.clinicViewed')}
                                  </div>
                                )}
                                {lifecycle === 'Offered' && (
                                  <div className="flex items-center gap-1 mt-1 text-[10px] text-purple-600 font-medium animate-pulse">
                                    <CheckCircle2 className="h-3 w-3" /> {t('patient.dashboard.offerReceived')}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell className="text-sm text-gray-600">
                                <div>{req.created_at ? formatDateLocale(req.created_at) : '-'}</div>
                                {req.updated_at && req.updated_at !== req.created_at && (
                                  <div className="text-[10px] text-slate-400">{t('patient.dashboard.updatedAgo', { time: timeAgo(req.updated_at) })}</div>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                <div className="flex justify-end gap-1">
                                  {req.has_offer ? (
                                    <Button
                                      size="sm"
                                      className="text-xs bg-purple-600 hover:bg-purple-700"
                                      onClick={() => navigate(
                                        req.offer_summary?.quote_id
                                          ? `/patient/quotes/${req.offer_summary.quote_id}`
                                          : '/patient/offers')}
                                      data-testid={`view-offer-${req.id}`}
                                    >
                                      <FileText className="h-3 w-3 mr-1" /> {t('patient.dashboard.viewOffer')}
                                    </Button>
                                  ) : (
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-xs"
                                      onClick={() => navigate('/patient/messages', {
                                        state: {
                                          // B-2.2 / INV-M15 — canonical lead → thread deep-link.
                                          // PatientMessagesV2's nav-state effect resolves the
                                          // existing thread via `t.context_id === contextId`;
                                          // the backend messaging service dedups new threads on
                                          // (clinic_id, context_type='lead', context_id=lead_id)
                                          // so this never creates a parallel thread.
                                          clinicId: req.clinic_id,
                                          contextId: req.id,
                                          contextType: 'lead',
                                          subject: `Request: ${req.treatment_display_name || req.treatment || t('patient.dashboard.generalInquiry')}`,
                                        },
                                      })}
                                      data-testid={`view-messages-${req.id}`}
                                    >
                                      <MessageCircle className="h-3 w-3 mr-1" /> {t('patient.dashboard.messages')}
                                    </Button>
                                  )}
                                  {req.payment_entry && (
                                    <Button size="sm" className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white" data-testid={`continue-payment-${req.id}`}
                                      onClick={() => navigate(`/patient/payment?quote_id=${req.payment_entry.quote_id}&order_id=${req.payment_entry.order_id}`)}>
                                      {t('patient.dashboard.continueToPayment')}
                                    </Button>
                                  )}
                                  {req.can_reopen && (
                                    <Button size="sm" variant="outline" className="text-xs" data-testid={`reopen-request-${req.id}`}
                                      onClick={async () => { try { await api.post(`/patient/quote-requests/${req.id}/reopen`); sonner.success(t("patient.dashboard.reopenSuccess")); fetchDashboardData({ silent: true }); } catch (e) { sonner.error(e?.response?.data?.message || t("common.error")); } }}>
                                      {t('patient.dashboard.reopenRequest')}
                                    </Button>
                                  )}
                                  {req.request_active !== false && !['Accepted', 'AcceptedAwaitingPayment', 'Closed'].includes(req.lifecycle) && (
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="text-xs text-slate-500 hover:text-red-600"
                                      onClick={() => handleCloseRequest(req)}
                                      data-testid={`close-request-${req.id}`}
                                    >
                                      {t('patient.dashboard.closeRequest')}
                                    </Button>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                  )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Offers from Clinics Tab — actionable state visual */}
          <TabsContent value="quotes">
            <Card className="border-l-4 border-l-blue-500">
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="h-4 w-4 text-blue-500" /> {t('patient.dashboard.offersFromClinics')}
                </CardTitle>
                <CardDescription className="text-xs">{t('patient.dashboard.offersFromClinicsDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {!sectionLoaded.offers && !sectionErrors.offers ? (
                  <SectionPending testid="offers-section-loading" />
                ) : sectionErrors.offers && !sectionLoaded.offers ? (
                  <SectionFailed testid="offers-section-failed" />
                ) : quotes.length === 0 ? (
                  <div className="text-center py-6 text-gray-500" data-testid="no-offers-state">
                    <ClipboardList className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-2">{t('patient.dashboard.noOffersYet')}</p>
                    <p className="text-sm mb-4">{t('patient.dashboard.noOffersHint')}</p>
                    <Button variant="outline" onClick={() => navigate('/find-clinics')} data-testid="browse-clinics-empty-btn">
                      {t('patient.dashboard.browseClinics')}
                    </Button>
                  </div>
                ) : (
                  <PatientOfferTable
                    quotes={quotes}
                    groups={offerGroups}
                    showHistory={showOfferHistory}
                    onShowHistory={setShowOfferHistory}
                    renderActions={(quote) => (
                      <Button size="sm" className="bg-blue-600 hover:bg-blue-700" onClick={() => navigate(`/patient/quotes/${quote.id}`)} data-testid={`view-offer-${quote.id}`}>
                        {t('patient.dashboard.view')}
                      </Button>
                    )}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Orders Tab */}
          <TabsContent value="orders">
            <Card>
              <CardHeader className="pb-2 pt-4">
                <CardTitle className="text-base">{t('patient.dashboard.tabMyOrders')}</CardTitle>
                <CardDescription className="text-xs">{t('patient.dashboard.myOrdersDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                {!sectionLoaded.orders && !sectionErrors.orders ? (
                  <SectionPending testid="orders-section-loading" />
                ) : sectionErrors.orders && !sectionLoaded.orders ? (
                  <SectionFailed testid="orders-section-failed" />
                ) : orders.length === 0 ? (
                  <div className="text-center py-6 text-gray-500" data-testid="no-orders-state">
                    <ShoppingCart className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p className="text-lg font-medium mb-2">{t('patient.dashboard.noOrdersYet')}</p>
                    <p className="text-sm">{t('patient.dashboard.noOrdersHint')}</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t('patient.dashboard.colOrderId')}</TableHead>
                          <TableHead>{t('patient.dashboard.colClinic')}</TableHead>
                          <TableHead>{t('patient.dashboard.colTotalPrice')}</TableHead>
                          <TableHead>{t('patient.dashboard.colStatus')}</TableHead>
                          <TableHead>{t('patient.dashboard.colCreated')}</TableHead>
                          <TableHead className="text-right">{t('patient.dashboard.colActions')}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {orders.map((order) => {
                          const pay = orderPayments[order.id] || { state: 'loading' };
                          const elig = pay.data || {};
                          return (
                          <TableRow key={order.id} className="hover:bg-purple-50/50" data-testid={`order-row-${order.id}`}>
                            <TableCell className="font-mono text-xs">
                              ...{order.id.slice(-8)}
                            </TableCell>
                            <TableCell>
                              <div className="font-medium" data-testid={`order-clinic-${order.id}`}>{order.clinic_info?.clinic_name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{order.clinic_info?.country}</div>
                            </TableCell>
                            <TableCell className="font-semibold text-purple-600" data-testid={`order-total-${order.id}`}>
                              {formatMoney(order.total_price, order.currency)}
                            </TableCell>
                            <TableCell>
                              <Badge className={getOrderStatusColor(order.status)} data-testid={`order-status-${order.id}`}>
                                {t(`patient.orderStatus.${order.status}`, { defaultValue: String(order.status || '').replace(/_/g, ' ') })}
                              </Badge>
                              {/* Payment state is a separate fact from the order's
                                  own status — a failed read is never "paid". */}
                              <div className="mt-1 text-[10px]" data-testid={`order-payment-state-${order.id}`}>
                                {pay.state === 'loading' && (
                                  <span className="text-slate-400">{t('patient.orderDetail.checkingPayment')}</span>
                                )}
                                {pay.state === 'payable' && (
                                  <span className="text-emerald-700 font-medium">
                                    {t('patient.orderDetail.dueNow')}: {formatMoney(elig.amount, elig.currency || order.currency)}
                                  </span>
                                )}
                                {pay.state === 'settled' && <span className="text-green-700">{t('patient.orderDetail.fullyPaidShort')}</span>}
                                {pay.state === 'scheduled' && <span className="text-sky-700">{t('patient.orderDetail.scheduledShort')}</span>}
                                {pay.state === 'incomplete' && <span className="text-amber-700">{t('patient.orderDetail.planIncompleteShort')}</span>}
                                {pay.state === 'activation_pending' && <span className="text-sky-700">{t('patient.orderDetail.activationPendingShort')}</span>}
                                {pay.state === 'blocked_identity' && <span className="text-amber-700">{t('patient.orderDetail.identityReviewShort')}</span>}
                                {(pay.state === 'unknown' || pay.state === 'incomplete' || pay.state === 'activation_pending') && (
                                  <button
                                    type="button"
                                    className="ml-1 underline text-slate-600"
                                    onClick={() => resolveRemainingOrderStates(true)}
                                    data-testid={`order-payment-retry-${order.id}`}
                                  >
                                    {t('patient.common.retry', 'Retry')}
                                  </button>
                                )}
                                {pay.state === 'unknown' && <span className="text-slate-500">{t('patient.orderDetail.paymentStateUnknownShort')}</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {formatDateLocale(order.created_at)}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1 flex-wrap">
                                {pay.state === 'payable' && (
                                  <Button
                                    size="sm"
                                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                    onClick={() => navigate(paymentLinkFor(order.id, elig.quote_id || order.quote_id))}
                                    data-testid={`order-continue-payment-${order.id}`}
                                  >
                                    {t('patient.dashboard.continueToPayment')}
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  className="bg-purple-600 hover:bg-purple-700"
                                  onClick={() => navigate(`/patient/orders/${order.id}`)}
                                  data-testid={`view-order-${order.id}`}
                                >
                                  {t('patient.dashboard.viewOrder')}
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Aftercare Tab — Phase 2 */}
          <TabsContent value="aftercare">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <HeartPulse className="h-5 w-5 text-emerald-600" /> {t('patient.dashboard.yourAftercare')}
                </CardTitle>
                <CardDescription>{t('patient.dashboard.aftercareDesc')}</CardDescription>
              </CardHeader>
              <CardContent>
                <AftercarePanel
                  patientId={user?.id || user?.user_id}
                  userRole="patient"
                />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      
      {/* P3: Build stamp removed for production */}
    </div>
  );
};

export default PatientDashboard;
